import { type AfterViewChecked, Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import { ToastService } from '@app/core/toast.service';
import { MessageService, type ToastMessageOptions } from 'primeng/api';
import { distinctUntilChanged, filter, type Subscription } from 'rxjs';
import { type ToastCloseEvent, ToastModule } from 'primeng/toast';
import { InboxService } from '@app/core/inbox.service';
import { DbTable } from '@app/models/models.helpers';

type InboxToastPayload = Partial<DbTable<'inbox_items'>>;
@Component({
  selector: 'app-toaster',
  standalone: true,
  imports: [
    ToastModule,
  ],
  templateUrl: './toaster.component.html',
  styleUrl: './toaster.component.scss',
  providers: [MessageService]
})
export class ToasterComponent implements OnInit, AfterViewChecked, OnDestroy {
  messageService = inject(MessageService);
  toastService = inject(ToastService);
  inboxService = inject(InboxService);

  subscription: Subscription;

  ngOnInit() {
  }

  ngAfterViewChecked() {
    this.subscription = this.toastService.messageQueue$.pipe(
      filter(queue => queue.length > 0),
      distinctUntilChanged((a, b) => a.length === b.length),
    ).subscribe((queue) => {
      console.log('in toaster, queue', queue);
      if (queue.length > 0) {
        const message = queue[0]; // Show the first message
        console.log('adding message', message);
        this.messageService.add(this.normalizeMessageSeverity(message));
        this.toastService.removeFirst();
      }
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public add(message: ToastMessageOptions) {
    this.messageService.add(this.normalizeMessageSeverity(message));
  }

  public clear() {
    this.messageService.clear();
  }

  public onClose(event: ToastCloseEvent) {
    console.log('onClose', event);
    if (event.message) {
      if (event.message.id) {
        this.inboxService.markAsToasted(event.message.id);
      }
    }

  }

  isActionableToast(message: ToastMessageOptions): boolean {
    const data = this.getInboxToastPayload(message);
    return this.inboxService.isActionable(data);
  }

  async onToastClick(message: ToastMessageOptions): Promise<void> {
    console.log('onToastClick', message);
    const data = this.getInboxToastPayload(message);
    await this.inboxService.handleClickAction(data, {
      markAsRead: true,
      markAsToasted: true,
    });
  }

  private normalizeMessageSeverity(message: ToastMessageOptions): ToastMessageOptions {
    const normalized = (message.severity ?? 'info').toString().toLowerCase();
    const severityMap: Record<string, ToastMessageOptions['severity']> = {
      primary: 'info',
      help: 'info',
      danger: 'error',
      warning: 'warn',
      warn: 'warn',
      error: 'error',
      info: 'info',
      success: 'success',
      secondary: 'secondary',
      contrast: 'contrast',
    };
    return {
      ...message,
      severity: severityMap[normalized] ?? 'info',
    };
  }

  private getInboxToastPayload(message: ToastMessageOptions): InboxToastPayload | null {
    if (!message.data || typeof message.data !== 'object') return null;
    return message.data as InboxToastPayload;
  }
}
